<template>
  <div>
    <v-dialog
      v-model="showMyModal"
      :max-width="popup?.modal_width ? `${popup.modal_width}px` : '500px'"
    >
      <v-card>
        <v-card-title class="headline d-flex justify-space-between align-center">
          {{ popup?.title }}
          <v-btn icon @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="my-4">
          <div v-html="popup?.text" />
        </v-card-text>

        <v-card-actions class="d-flex justify-space-between">
          <v-checkbox v-model="dontShowAgain" label="Não mostrar mais" hide-details dense />
          <v-btn
            v-if="popup?.button_text"
            :color="popup?.button_color || 'primary'"
            @click="goToPopupLink"
          >
            {{ popup?.button_text }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container fluid class="py-8 px-6">
      <DocumentModal ref="documentViewer" :pdfBase64="pdfBase64" :title="documentTitle" />
      <div class="masonry">
        <div class="block" v-if="resume && resume.title && selectedCompany">
          <v-card flat :loading="isLoading" class="mx-auto mb-3 px-3" max-width="374">
            <v-card-title class="text-h6" style="word-break: break-word">
              Resumo mensal do contrato
              <br />
              <small style="line-height: 1.2; font-size: 0.8rem">{{ resume.title }} </small>
            </v-card-title>
            <v-divider v-if="isAdmin" class="mx-4" />
            <v-card-text v-if="isAdmin">
              <v-autocomplete
                dense
                :items="getYearMonthList(selectedCompany.contract.created_at)"
                v-model="date"
                label="Mês"
                hide-details
                @input="getResume"
              />
            </v-card-text>
            <v-divider class="mx-4" />
            <v-card-text v-if="resume.documents">
              <strong class="text-body-2">Documentos</strong>: {{ resume.documents.total }} /
              {{ resume.documents.limit }}
              <p class="text-caption" style="word-break: break-word">
                Documentos finalizados contam para o total
              </p>
            </v-card-text>
            <v-divider class="mx-4" />
            <v-card-text v-if="resume.whatsapp">
              <strong class="text-body-2">WhatsApp</strong>: {{ resume.whatsapp.total }} /
              {{ resume.whatsapp.limit }}
              <p class="text-caption" style="word-break: break-word">
                Envios realizados via WhatsApp
              </p>
            </v-card-text>
            <v-divider class="mx-4" />
            <v-card-text v-if="resume.space">
              <strong class="text-body-2">Espaço ocupado</strong>: {{ resume.space.total }} MB
              <p class="text-caption" style="word-break: break-word">
                Documentos finalizados e backups contam para o total
              </p>
            </v-card-text>
            <v-divider
              class="mx-4"
              v-if="selectedCompany && selectedCompany.state === 'RS' && resume.jucis"
            />
            <v-card-text v-if="selectedCompany && selectedCompany.state === 'RS' && resume.jucis">
              <strong class="text-body-2">Autenticações via API</strong>: {{ resume.jucis.total }}
              <p class="text-caption" style="word-break: break-word">
                Livros autenticados na Junta Comercial via API
              </p>
            </v-card-text>
          </v-card>
        </div>
        <div class="block">
          <v-card flat :loading="isLoading" class="mx-auto mb-3 px-3" max-width="374">
            <v-card-title class="text-h6">Olá, {{ name }}.</v-card-title>
            <v-divider class="mx-4" />
            <v-list flat>
              <v-subheader v-if="!selectedCompany" class="text-body-2">
                Comece escolhendo a sua empresa no seletor acima.
              </v-subheader>
              <v-list-item-group v-if="isAdminOrEditor">
                <v-list-item
                  :to="{ name: 'signers' }"
                  v-if="!isLoading && selectedCompany && !signersCount"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-account-group</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">Assinantes</v-list-item-title>
                    <small class="text-caption">
                      Você ainda não possui nenhum assinante. <br />Comece adicionando quem irá
                      assinar os documentos clicando aqui.
                    </small>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  :to="{ name: 'books' }"
                  v-if="!isLoading && selectedCompany && signersCount && !booksCount"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-book-open-variant</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">Livros</v-list-item-title>
                    <small class="text-caption">
                      Você ainda não possui nenhum livro. <br />Adicione clicando aqui.
                    </small>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  inactive
                  :ripple="false"
                  v-if="!isLoading && selectedCompany && signersCount && booksCount"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">
                      Nenhuma ação pendente.
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-card>
        </div>
        <div class="block" v-if="!isLoading && selectedCompany">
          <v-card flat :loading="isLoading" class="mx-auto mb-3 px-3" max-width="374">
            <v-card-title class="text-h6" style="word-break: break-word">
              Documentos pendentes
            </v-card-title>
            <v-divider class="mx-4" />
            <v-card-text class="px-0" style="max-height: 500px; overflow-y: auto">
              <v-list flat v-if="!isLoading && documentsWaiting && documentsWaiting.length">
                <v-list-item
                  v-for="(document, i) in documentsWaiting"
                  :key="i"
                  :to="{
                    name: 'books-view',
                    params: { id: document.book_id },
                    query: { q: encodeURI(document.title) },
                  }"
                >
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">{{ document.title }}</v-list-item-title>
                    <br />
                    <small class="text-caption">
                      {{ document.status }}
                    </small>
                    <small class="text-caption">
                      Criado em {{ fullDateTime(document.created_at) }}
                      <v-icon
                        small
                        class="ml-1"
                        style="vertical-align: top;"
                      >
                        mdi-open-in-new
                      </v-icon>
                    </small>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-list flat v-if="!isLoading && documentsWaiting && !documentsWaiting.length">
                <v-list-item inactive :ripple="false">
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">
                      Nenhum documento pendente.
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </div>
        <div class="block" v-if="!isLoading && selectedCompany && hasModule('gestao_societaria')">
          <v-card flat :loading="isLoading" class="mx-auto mb-3 px-3" max-width="374">
            <v-card-title class="text-h6" style="word-break: break-word">
              Documentos próximos a vencer
            </v-card-title>
            <v-divider class="mx-4" />
            <v-card-text class="px-0" style="max-height: 500px; overflow-y: auto">
              <v-list flat v-if="!isLoading && documentsExpires && documentsExpires.length">
                <v-list-item v-for="(document, i) in documentsExpires" :key="i">
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">{{ document.title }}</v-list-item-title>
                    <small class="text-caption">Vence em: {{ document.expires_at }}</small>
                    <a
                      href="#"
                      @click.prevent="preview(document)"
                      class="pb-5 text-primary d-flex align-center no-underline text-caption"
                      style="text-decoration: none"
                    >
                      <v-icon small class="mr-1">mdi-eye</v-icon>
                      Visualizar
                    </a>
                    <small class="text-caption">
                      Criado em {{ fullDateTime(document.created_at) }}
                    </small>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-list flat v-if="!isLoading && documentsExpires && !documentsExpires.length">
                <v-list-item inactive :ripple="false">
                  <v-list-item-icon>
                    <v-icon>mdi-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2"
                    >Nenhum documento a vencer.</v-list-item-title
                    >
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </div>
        <div class="block" v-if="!isLoading && selectedCompany && hasModule('gestao_societaria')">
          <v-card flat :loading="isLoading" class="mx-auto mb-3 px-3" max-width="374">
            <v-card-title class="text-h6" style="word-break: break-word">
              Próximos eventos agendados
            </v-card-title>
            <v-divider class="mx-4" />
            <v-card-text class="px-0" style="max-height: 500px; overflow-y: auto">
              <v-list flat v-if="!isLoading && nextMeetings && nextMeetings.length">
                <v-list-item
                  v-for="(meeting, i) in nextMeetings"
                  :key="i"
                  :to="{
                    name: 'meeting-view',
                    params: { id: meeting.id },
                    query: { q: encodeURI(meeting.name) },
                  }"
                >
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">{{ meeting.name }}</v-list-item-title>
                    <br />
                    <span class="text-caption"
                    >{{ meeting.format === 1 ? 'Digital' : 'Presencial' }} -
                      {{ meeting.location }}</span
                    >
                    <br />
                    <span class="text-caption">{{ formatDateRange(meeting.date) }}</span>
                    <br />
                    <small :class="[getStatusInfo(meeting?.status).color, 'text-caption']">
                      {{ getStatusInfo(meeting?.status).name }}
                    </small>
                    <small class="text-caption">
                      Criado em {{ fullDateTime(meeting.created_at) }}
                    </small>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-list flat v-if="!isLoading && nextMeetings && !nextMeetings.length">
                <v-list-item inactive :ripple="false">
                  <v-list-item-icon>
                    <v-icon>mdi-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2"
                    >Nenhum evento agendado pendente.</v-list-item-title
                    >
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </div>
        <div class="block" v-if="!isLoading && selectedCompany && logs.length">
          <v-card flat :loading="isLoading" class="mx-auto mb-3 px-3" max-width="374">
            <v-card-title class="text-h6" style="word-break: break-word">
              Últimas movimentações
            </v-card-title>
            <v-divider v-if="isAdmin" class="mx-4" />
            <v-card-text class="px-0" v-if="isAdmin" style="max-height: 500px; overflow-y: auto">
              <v-list flat>
                <v-list-item v-for="(log, i) in logs" :key="i">
                  <v-list-item-content class="pt-1">
                    <span class="text-body-2">{{ log.description }}</span>
                    <br />
                    <small>
                      Por {{ log.user ? log.user.name : 'Usuário excluído' }}
                    </small>
                    <small class="text-caption"> Em {{ fullDateTime(log.created_at) }} </small>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import formatLogs from '@/mixins/formatLogs';
import DocumentModal from '@/components/modals/DocumentModal.vue';
import {
  fullDateTime, yearMonth, formatDateRange, formatDateToDisplay,
} from '@/plugins/dayjs';
import hasModule from '@/mixins/hasModule';

export default {
  name: 'Home',
  components: { DocumentModal },
  data: () => ({
    isLoading: false,
    date: yearMonth(),
    pdfBase64: null,
    documentTitle: '',
    showMyModal: false,
    dontShowAgain: false,
  }),

  mixins: [formatLogs, hasModule],

  computed: {
    user() {
      return this.$store.getters['auth/user'];
    },
    name() {
      if (this.user) {
        const split = this.user?.name?.split(' ');
        return split[0];
      }
      return '';
    },
    selectedCompany() {
      return this.$store.state.company.selectedCompany;
    },
    signersCount() {
      return this.$store.state.signer.count;
    },
    booksCount() {
      return this.$store.state.book.count;
    },
    resume() {
      return this.$store.state.contract.items;
    },
    isAdminOrEditor() {
      return (
        this.$store.getters['auth/check']('administrator') ||
        this.$store.getters['auth/check']('editor')
      );
    },
    isAdmin() {
      return this.$store.getters['auth/check']('administrator');
    },
    logs() {
      return this.$store.state.log.items;
    },
    popup() {
      return this.$store.state.promotion.items || null;
    },
    dates() {
      return [];
    },
    documentsWaiting() {
      return this.$store.getters['document/itemsWaiting'];
    },
    documentsExpires() {
      return this.$store.getters['document/itemsExpires'];
    },
    nextMeetings() {
      return this.$store.getters['meeting/nextMeetings'];
    },
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      const result = await this.$store.dispatch('promotion/index');

      if (!result || !result.active) {
        this.showMyModal = false;
      } else {
        const hiddenPopups = JSON.parse(localStorage.getItem('hiddenPopups')) || [];
        if (result.id && hiddenPopups.includes(result.id)) {
          this.showMyModal = false;
        } else {
          this.showMyModal = true;
        }
      }

      if (this.selectedCompany && this.isAdminOrEditor) {
        try {
          this.isLoading = true;
          await this.$store.dispatch('signer/count');
          if (this.$store.getters['auth/hasModule']('livros_societarios')) {
            await this.$store.dispatch('book/count');
          }

          if (this.hasModule('gestao_societaria')) {
            await this.$store.dispatch('meeting/indexMeetings');
            await this.$store.dispatch('document/indexExpires');
          }

          await this.$store.dispatch('document/indexByStatus', ['WAITING_SIGNERS', 'WAITING_SIGNATURES']);
          if (this.$store.getters['auth/check']('administrator')) {
            await this.$store.dispatch('log/index', { limit: 10 });
            await this.$store.dispatch('contract/resume', { date: this.date });
          }
        } catch (error) {
          this.$toasted.error(error.message);
        } finally {
          this.isLoading = false;
        }
      }
    },

    closeModal() {
      this.showMyModal = false;
      if (this.dontShowAgain && this.popup?.id) {
        const hiddenPopups = JSON.parse(localStorage.getItem('hiddenPopups')) || [];
        hiddenPopups.push(this.popup.id);
        localStorage.setItem('hiddenPopups', JSON.stringify(hiddenPopups));
      }
    },

    async getResume() {
      await this.$store.dispatch('contract/resume', { date: this.date });
    },
    getYearMonthList(startDate) {
      const list = [];
      const currentDate = new Date();
      const startDateFix = startDate.replace(' ', 'T');
      const currentMonth = new Date(startDateFix);

      while (currentMonth <= currentDate) {
        const year = currentMonth.getFullYear();
        const month = (currentMonth.getMonth() + 1).toString().padStart(2, '0');
        const yearM = `${year}-${month}`;

        list.push(yearM);

        currentMonth.setMonth(currentMonth.getMonth() + 1);
      }

      return list.reverse();
    },

    getStatusInfo(status) {
      const statuses = {
        0: { name: 'Pendente', color: 'red--text' },
        1: { name: 'Concluído', color: 'grey--text' },
      };
      return statuses[status] || { name: 'Desconhecido', color: 'black--text' };
    },

    async preview(document) {
      try {
        this.isLoading = true;
        const response = await this.$store.dispatch('document/getPdf', {
          id: document.id,
          folder: document.folder,
          filename: document.filename,
        });

        this.$refs.documentViewer.openModal(response);
        this.documentTitle = document.title;
        this.isLoading = false;
      } catch (error) {
        this.$toasted.error('Erro ao visualizar o documento');
        this.isLoading = false;
      }
    },

    goToPopupLink() {
      if (this.popup?.button_link) {
        window.open(this.popup.button_link, '_blank');
      }
      this.showMyModal = false;
      if (this.dontShowAgain) {
        localStorage.setItem('hidePopup', 'true');
      }
    },
    fullDateTime,
    formatDateRange,
    formatDateToDisplay,
  },
};
</script>
<style scoped>
.masonry {
  column-count: 1;
  column-gap: 1rem;
}

.block {
  background: #f5f5f5;
  padding: 0.5rem;
  break-inside: avoid;
  display: block;
}

.v-card__title {
  line-height: 1.2;
}

@media (min-width: 768px) {
  .masonry {
    column-count: 2;
  }
}

@media (min-width: 1110px) {
  .masonry {
    column-count: 3;
  }
}

@media (min-width: 1400px) {
  .masonry {
    column-count: 4;
  }
}
</style>
